/**
 * O trecho aberto no próprio documento (E202.F2, GUS-145, ADR-0145 §5–§6): o editor no lugar do texto
 * (a prévia de trabalho: o que se digita aparece ali) e a faixa de ação embaixo. O rascunho vai ao
 * servidor a cada pausa; ao fechar (Esc, "Fechar" ou outro bloco aberto) com mudança pendente, ele é
 * guardado na hora. "Salvar" grava a versão; a falha e a recusa mantêm o texto e o bloco aberto.
 *
 * E202.F2.C01/C02/C04: quem monta o editor dá a chave do trecho (trocar de campo remonta, e o rascunho
 * do anterior vai para o próprio alvo); o editor só monta com os rascunhos lidos (o guardado é o
 * começo dele; a falha da leitura tem "Tentar de novo"); e o bloco registra a guarda imediata que o
 * "Guardar o rascunho e sair" espera.
 */
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';

import { FalhaComSaida, Skeleton } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { ACOES_COM_TEXTO, type TrechoNaTelaData } from '@/lib/correcaoDoRascunho';
import type { ParagrafoDoBlocoData } from '@/lib/documentoDaSaida';
import type { ConteudoDoEditor, RascunhoNaTelaData } from '@/lib/editorManual';
import type { ModoDaEdicao } from '@/lib/markupDaEdicao';

import { FaixaDoBloco } from './FaixaDoBloco';
import { inicioDoBloco } from './inicioDoBloco';
import { PALETA } from './paleta';
import { useRascunho } from './rascunhoDoBloco';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa.edicao;

// O editor (TipTap, ~100 kB gzip) só carrega quando alguém abre um trecho: a mesa abre sem ele.
const EditorDoBloco = lazy(() =>
  import('./EditorDoBloco').then((m) => ({ default: m.EditorDoBloco })),
);

export interface ParteEmEdicaoProps {
  trecho: TrechoNaTelaData;
  paragrafos: readonly ParagrafoDoBlocoData[];
  modo: ModoDaEdicao;
  rascunho: RascunhoNaTelaData | undefined;
  edicao: EdicaoNoDocumento;
}

function mudou(a: ConteudoDoEditor, b: ConteudoDoEditor): boolean {
  return a.acao !== b.acao || a.texto !== b.texto || a.fundamento !== b.fundamento;
}

function Moldura({ trecho, children }: { trecho: TrechoNaTelaData; children: ReactNode }) {
  return (
    <div
      className="-mx-2 my-1 border-l-2 bg-white px-2 ring-1"
      style={{ borderColor: PALETA.ciano, ['--tw-ring-color' as string]: PALETA.linha }}
      data-trecho={trecho.id}
      data-testid="parte-em-edicao"
    >
      <p
        className="pt-1 text-[0.72em] font-medium uppercase tracking-[0.08em]"
        style={{ color: PALETA.navy2 }}
      >
        {trecho.rotulo} · {t.emEdicao}
      </p>
      {children}
    </div>
  );
}

export function ParteEmEdicao(props: ParteEmEdicaoProps): JSX.Element {
  const { trecho, edicao } = props;
  if (edicao.carga === 'lendo') {
    return (
      <Moldura trecho={trecho}>
        <Skeleton className="h-16 w-full" data-testid="parte-lendo-rascunhos" />
      </Moldura>
    );
  }
  if (edicao.carga === 'falhou') {
    return (
      <Moldura trecho={trecho}>
        <FalhaComSaida
          mensagem={t.rascunhosFalhou}
          onTentarDeNovo={edicao.tentarRascunhos}
          data-testid="parte-rascunhos-falhou"
        />
      </Moldura>
    );
  }
  return <ParteComRascunhos {...props} />;
}

function ParteComRascunhos({
  trecho,
  paragrafos,
  modo,
  rascunho,
  edicao,
}: ParteEmEdicaoProps): JSX.Element {
  // O começo é o do clique: a releitura do documento não troca o que está no editor.
  const [inicio] = useState(() => inicioDoBloco(trecho, paragrafos, modo, rascunho, edicao.pedida));
  const [conteudo, setConteudo] = useState(inicio.conteudo);
  const { estado, situacao, cancelar, guardarJa } = useRascunho(
    conteudo,
    (c) => edicao.guardar(trecho, c),
    rascunho?.situacao ?? null,
  );
  const sujo = rascunho !== undefined || mudou(conteudo, inicio.conteudo);
  const { marcarSujo, registrarGuarda } = edicao;
  useEffect(() => marcarSujo(sujo), [sujo, marcarSujo]);
  const atual = useRef(conteudo);
  atual.current = conteudo;
  useEffect(() => {
    // A guarda lê o conteúdo de agora a cada volta: o que se digita durante a espera também vai.
    registrarGuarda(() => guardarJa(() => atual.current));
    return () => registrarGuarda(null);
  }, [registrarGuarda, guardarJa]);
  const comTexto = ACOES_COM_TEXTO.has(conteudo.acao);

  return (
    <Moldura trecho={trecho}>
      <div className={comTexto ? undefined : 'hidden'}>
        <Suspense fallback={<Skeleton className="h-16 w-full" />}>
          <EditorDoBloco
            inicial={inicio.doc}
            modo={modo}
            rotulo={trecho.rotulo}
            onMudar={(texto) => setConteudo((c) => ({ ...c, texto }))}
            onEsc={edicao.fechar}
          />
        </Suspense>
      </div>
      <FaixaDoBloco
        trecho={trecho}
        conteudo={conteudo}
        onMudar={setConteudo}
        noDocumento={inicio.noDocumento}
        estado={estado}
        situacao={situacao}
        salvando={edicao.salvando}
        falha={edicao.falha}
        onSalvar={() => {
          cancelar();
          edicao.salvar(trecho, conteudo);
        }}
        onDescartar={() => {
          cancelar();
          edicao.descartar(trecho);
        }}
        onFechar={edicao.fechar}
      />
    </Moldura>
  );
}
